import React, { useEffect, useState } from 'react';
import { Pie } from '@antv/g2plot';
import { useSelector } from 'react-redux';
import { Row, Col, Typography } from '~/components';
import { GraficoPie, CardWrapper, NoDataContainer, CardTitleHeading } from './style';
import { useWindowListener, tiposDevice } from './windowListener.component';
import Filter from './Filter.component';
import ExercicioSelect from './ExercicioSelect.component';
import VisualizarGridButton from './VisualizarGridButton.component';

function PieGraphic({
  id,
  titulo,
  data,
  legenda,
  tootltipTitle,
  ValorLink,
  hasFilter,
  filterDescription,
  contentFilter,
  showEyeButton = false,
  corFonte,
  colorField = 'EixoX',
  angleField = 'EixoY',
  legendPosition = 'left',
  tooltipFormatter,
  legendMaxRow,
  legendItemMarginBottom,
  legendItemSpacing,
  showExercicioSelect,
  exercicio,
}) {
  const [pie, setPie] = useState(null);
  const windowListener = useWindowListener();
  const {
    graficHighContrastStyle,
    highContrast,
    graficDefaultStyle,
    zoomFonte,
  } = useSelector(state => state.configuration);
  const getConfigStyle = highContrast
    ? graficHighContrastStyle
    : graficDefaultStyle;
  const legend = {
    visible: true,
    maxRow: legendMaxRow,
    itemMarginBottom: legendItemMarginBottom,
    itemSpacing: legendItemSpacing,
    position: legendPosition,
    maxItemWidth: 160,
    title: {
      visible: true,
      text: legenda,
      style: getConfigStyle,
    },
    itemName: {
      formatter: v => {
        return v.trim().length >= 27 ? `${v.substring(0, 27)}...` : v.trim();
      },
      style: getConfigStyle,
    },
  };
  const [config] = useState({
    title: false,
    legend,
    autoFit: true,
    radius: 0.8,
    label: {
      autoHide: true,
      type: 'spider',
      formatter: ({ percent }) => `${(percent * 100).toFixed(2)}%`,
      style: getConfigStyle,
    },
    statistic: {
      visible: false,
    },
    colorField,
    angleField,
    color: [
      '#30BBAA',
      '#F39366',
      '#876AB0',
      '#C9A227',
      '#77C966',
      '#EE7B80',
      '#7F8BB2',
      '#D4A017',
      '#D85B81',
      '#A8B32E',
      '#2DA0D7',
      '#FBB141',
    ],
    tooltip: {
      showTitle: true,
      title: tootltipTitle,
      formatter:
        tooltipFormatter ||
        (({ EixoX, EixoY }) => {
          return {
            name: EixoX,
            value: new Intl.NumberFormat('pt-BR').format(EixoY),
          };
        }),
    },
  });

  useEffect(() => {
    if (pie) return;

    const element = document.getElementById(id);
    if (element) {
      const pieRender = new Pie(element, { data, ...config });
      setPie(pieRender);
      pieRender.render();
    }
  }, [config, data, id, pie]);

  useEffect(() => {
    if (pie) {
      pie.update({
        data,
      });
      pie.render();
    }
  }, [data, pie]);

  useEffect(() => {
    if (windowListener && windowListener.device && pie) {
      const visible = windowListener.device === tiposDevice.desktop;
      pie.update({
        legend: visible ? legend : false,
        label: {
          ...config.label,
          style: getConfigStyle,
        },
      });
    }

    if (pie) pie.render();
  }, [
    pie,
    windowListener,
    highContrast,
    config.title,
    config.text,
    config.label,
    getConfigStyle,
    zoomFonte,
  ]);

  const hasNoData = !data || data.length === 0;

  return (
    <>
      <CardWrapper
        title={<CardTitleHeading>{titulo}</CardTitleHeading>}
        extra={
          <>
            {showExercicioSelect && <ExercicioSelect id={id} />}
            {showEyeButton && (
              <VisualizarGridButton
                to={ValorLink || 'notfound'}
                corFonte={corFonte}
              />
            )}
          </>
        }
        size="small"
      >
        <Row>
          {hasFilter && (
            <Col flex="45px" style={{ display: 'flex' }}>
              <Filter
                idGrafico={id}
                contentFilter={contentFilter}
                exercicio={exercicio}
              />
            </Col>
          )}
          {filterDescription && (
            <Col flex="auto">
              <Typography.Text
                style={{
                  position: 'relative',
                  top: -6,
                }}
              >
                {filterDescription}
              </Typography.Text>
            </Col>
          )}
        </Row>
        <Row style={{ flex: 1, minHeight: 'inherit' }}>
          {hasNoData ? (
            <NoDataContainer>
              <Typography.Text>
                Não há dados disponíveis para exibição
              </Typography.Text>
            </NoDataContainer>
          ) : (
            <div
              role="img"
              aria-label={titulo ? `Gráfico: ${titulo}` : 'Gráfico de pizza'}
            >
              <GraficoPie id={id} />
            </div>
          )}
        </Row>
      </CardWrapper>
    </>
  );
}

export default PieGraphic;
